/**
 * SocialShareModal.jsx - Universal social sharing component
 * 
 * Supports: WhatsApp, Instagram, LinkedIn, X (Twitter), Facebook, Copy Link, Native Share
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Link2, MessageCircle, Share2, Download } from 'lucide-react';

// Social platform configurations
const SOCIAL_PLATFORMS = [
    {
        id: 'whatsapp',
        name: 'WhatsApp',
        color: '#25D366',
        icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
        ),
        getUrl: (text, url) => `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`
    },
    {
        id: 'telegram',
        name: 'Telegram',
        color: '#0088cc',
        icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
        ),
        getUrl: (text, url) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
    },
    {
        id: 'twitter',
        name: 'X',
        color: '#000000',
        icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
        ),
        getUrl: (text, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
    },
    {
        id: 'facebook',
        name: 'Facebook',
        color: '#1877F2',
        icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
        ),
        getUrl: (text, url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`
    },
    {
        id: 'linkedin',
        name: 'LinkedIn',
        color: '#0A66C2',
        icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
        ),
        getUrl: (text, url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    },
    {
        id: 'instagram',
        name: 'Instagram',
        color: '#E4405F',
        icon: (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.757-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
            </svg>
        ),
        // Instagram doesn't have a direct share URL - opens app or prompts download
        getUrl: null,
        action: 'instagram'
    }
];

const SocialShareModal = ({ 
    isOpen, 
    onClose, 
    shareText = '', 
    shareUrl = '', 
    shareTitle = '',
    imageUrl = null,
    onDownload = null,
    language = 'en'
}) => {
    const isSpanish = language?.startsWith('es');
    const [copied, setCopied] = useState(false);
    const [showInstagramTip, setShowInstagramTip] = useState(false);
    
    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl || window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };
    
    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: shareUrl
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Share failed:', err);
                }
            }
        }
    };
    
    const handlePlatformClick = (platform) => {
        if (platform.action === 'instagram') {
            // Instagram doesn't support direct URL sharing
            // Show tip to download and share manually
            setShowInstagramTip(true);
            if (onDownload) {
                onDownload();
            }
            return;
        }
        
        if (platform.getUrl) {
            const url = platform.getUrl(shareText, shareUrl || window.location.href);
            window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
        }
    };
    
    if (!isOpen) return null;
    
    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <motion.div 
                    className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    onClick={onClose}
                />
                
                <motion.div
                    className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl overflow-hidden"
                    style={{
                        background: 'linear-gradient(145deg, rgba(15,15,18,0.98) 0%, rgba(8,8,12,0.99) 100%)',
                        border: '1px solid rgba(212,175,55,0.2)'
                    }}
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    onClick={e => e.stopPropagation()}
                    data-testid="social-share-modal"
                >
                    {/* Header */}
                    <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                        <h3 className="text-lg font-cinzel text-primary">
                            {isSpanish ? 'Compartir' : 'Share'}
                        </h3>
                        <button 
                            onClick={onClose}
                            className="p-2 rounded-full hover:bg-white/10 transition-colors"
                        >
                            <X size={18} className="text-white/60" />
                        </button>
                    </div>
                    
                    {/* Social Platforms Grid */}
                    <div className="p-5">
                        <div className="grid grid-cols-3 gap-3">
                            {SOCIAL_PLATFORMS.map(platform => (
                                <button
                                    key={platform.id}
                                    onClick={() => handlePlatformClick(platform)}
                                    className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:scale-105 active:scale-95"
                                    style={{
                                        background: `${platform.color}15`,
                                        border: `1px solid ${platform.color}30`
                                    }}
                                    data-testid={`share-${platform.id}`}
                                >
                                    <div style={{ color: platform.color }}>
                                        {platform.icon}
                                    </div>
                                    <span className="text-[10px] text-white/70 font-inter">
                                        {platform.name}
                                    </span>
                                </button>
                            ))}
                        </div>
                        
                        {/* Instagram Tip */}
                        <AnimatePresence>
                            {showInstagramTip && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-4 p-3 rounded-lg bg-pink-500/10 border border-pink-500/20"
                                >
                                    <p className="text-xs text-pink-300">
                                        {isSpanish 
                                            ? '📸 Imagen descargada. Abre Instagram y compártela en tu historia o feed.'
                                            : '📸 Image downloaded. Open Instagram and share it to your story or feed.'}
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        
                        {/* Divider */}
                        <div className="flex items-center gap-3 my-5">
                            <div className="flex-1 h-px bg-white/10" />
                            <span className="text-[10px] text-white/30 uppercase tracking-wider">
                                {isSpanish ? 'o' : 'or'}
                            </span>
                            <div className="flex-1 h-px bg-white/10" />
                        </div>
                        
                        {/* Copy Link & Native Share */}
                        <div className="flex gap-3">
                            {/* Copy Link */}
                            <button
                                onClick={handleCopyLink}
                                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all"
                                style={{
                                    background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)',
                                    border: copied ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(255,255,255,0.1)'
                                }}
                            >
                                {copied ? (
                                    <>
                                        <Check size={16} className="text-green-400" />
                                        <span className="text-xs text-green-400">
                                            {isSpanish ? '¡Copiado!' : 'Copied!'}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Link2 size={16} className="text-white/60" />
                                        <span className="text-xs text-white/60">
                                            {isSpanish ? 'Copiar enlace' : 'Copy link'}
                                        </span>
                                    </>
                                )}
                            </button>
                            
                            {/* Native Share (if supported) */}
                            {navigator.share && (
                                <button
                                    onClick={handleNativeShare}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all"
                                    style={{
                                        background: 'rgba(212,175,55,0.1)',
                                        border: '1px solid rgba(212,175,55,0.3)'
                                    }}
                                >
                                    <Share2 size={16} className="text-primary" />
                                    <span className="text-xs text-primary">
                                        {isSpanish ? 'Más opciones' : 'More options'}
                                    </span>
                                </button>
                            )}
                        </div>
                        
                        {/* Download Button (if image available) */}
                        {onDownload && (
                            <button
                                onClick={onDownload}
                                className="w-full mt-3 flex items-center justify-center gap-2 py-3 rounded-xl transition-all hover:scale-[1.01]"
                                style={{
                                    background: 'rgba(96,165,250,0.1)',
                                    border: '1px solid rgba(96,165,250,0.3)'
                                }}
                            >
                                <Download size={16} className="text-blue-400" />
                                <span className="text-xs text-blue-400">
                                    {isSpanish ? 'Descargar imagen' : 'Download image'}
                                </span>
                            </button>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default SocialShareModal;
